import { useState } from "react";
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/useDebounce";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Search, Edit, Trash2, Eye, Users } from "lucide-react";
import { getCustomerDisplayName } from "@/lib/formatters";
import { CUSTOMER_TYPE_LABELS } from "@/config/constants";
import { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

type Customer = Tables<"customers">;
type CustomerInsert = TablesInsert<"customers">;
type CustomerUpdate = TablesUpdate<"customers">;
type Order = Tables<"orders">;

const initialFormData: Partial<CustomerInsert> = {
  type: "private",
  first_name: "",
  last_name: "",
  company_name: "",
  business_id: "",
  email: "",
  phone: "",
  address: "",
  address_line2: "",
  postal_code: "",
  city: "",
  country: "FI",
  contact_person: "",
  einvoice_address: "",
  einvoice_operator: "",
};

export default function Customers() {
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isOrdersDialogOpen, setIsOrdersDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [formData, setFormData] = useState<Partial<CustomerInsert>>(initialFormData);

  
  const queryClient = useQueryClient();
  const debouncedSearch = useDebounce(searchQuery, 300);

  const PAGE_SIZE = 50;

  // Fetch customers with pagination
  const customersQuery = useInfiniteQuery({
    queryKey: ["customers", debouncedSearch],
    queryFn: async ({ pageParam = 0 }) => {
      let query = supabase
        .from("customers")
        .select("*", { count: "exact" })
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .range(pageParam, pageParam + PAGE_SIZE - 1);

      if (debouncedSearch) {
        query = query.or(
          `first_name.ilike.%${debouncedSearch}%,last_name.ilike.%${debouncedSearch}%,company_name.ilike.%${debouncedSearch}%,email.ilike.%${debouncedSearch}%,phone.ilike.%${debouncedSearch}%,contact_person.ilike.%${debouncedSearch}%`
        );
      }

      const { data, error, count } = await query;
      if (error) throw error;
      return { data: data as Customer[], count: count ?? 0, nextOffset: pageParam + PAGE_SIZE };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.data.length < PAGE_SIZE) return undefined;
      return lastPage.nextOffset;
    },
  });

  const isLoading = customersQuery.isLoading;
  const isError = customersQuery.isError;
  const hasNextPage = customersQuery.hasNextPage;
  const isFetchingNextPage = customersQuery.isFetchingNextPage;
  const fetchNextPage = customersQuery.fetchNextPage;
  const customers = customersQuery.data?.pages.flatMap(p => p.data) ?? [];

  // Fetch customer orders
  const { data: customerOrders = [] } = useQuery({
    queryKey: ["customer-orders", selectedCustomer?.id],
    queryFn: async () => {
      if (!selectedCustomer) return [];
      const { data, error } = await supabase
        .from("orders")
        .select("*")
        .eq("customer_id", selectedCustomer.id)
        .is("deleted_at", null)
        .order("order_date", { ascending: false });

      if (error) throw error;
      return data as Order[];
    },
    enabled: !!selectedCustomer && isOrdersDialogOpen,
  });

  // Create customer mutation
  const createMutation = useMutation({
    mutationFn: async (data: CustomerInsert) => {
      const { error } = await supabase.from("customers").insert(data);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.success("Asiakas lisätty", {
        description: "Uusi asiakas on lisätty onnistuneesti.",
      });
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error("Virhe", {
        description: `Asiakkaan lisääminen epäonnistui: ${error.message}`,
      });
    },
  });

  // Update customer mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: CustomerUpdate }) => {
      const { error } = await supabase
        .from("customers")
        .update(data)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.success("Asiakas päivitetty", {
        description: "Asiakkaan tiedot on päivitetty onnistuneesti.",
      });
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error("Virhe", {
        description: `Asiakkaan päivittäminen epäonnistui: ${error.message}`,
      });
    },
  });

  // Delete customer mutation (soft delete)
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("customers")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      toast.success("Asiakas poistettu", {
        description: "Asiakas on poistettu onnistuneesti.",
      });
      setIsDeleteDialogOpen(false);
      setCustomerToDelete(null);
    },
    onError: (error) => {
      toast.error("Virhe", {
        description: `Asiakkaan poistaminen epäonnistui: ${error.message}`,
      });
    },
  });

  const handleOpenDialog = (customer?: Customer) => {
    if (customer) {
      setSelectedCustomer(customer);
      setFormData({
        type: customer.type,
        first_name: customer.first_name || "",
        last_name: customer.last_name || "",
        company_name: customer.company_name || "",
        business_id: customer.business_id || "",
        email: customer.email || "",
        phone: customer.phone,
        address: customer.address,
        address_line2: customer.address_line2 || "",
        postal_code: customer.postal_code,
        city: customer.city,
        country: customer.country || "FI",
        contact_person: customer.contact_person || "",
        einvoice_address: customer.einvoice_address || "",
        einvoice_operator: customer.einvoice_operator || "",
      });
    } else {
      setSelectedCustomer(null);
      setFormData(initialFormData);
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedCustomer(null);
    setFormData(initialFormData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.phone || !formData.address || !formData.postal_code || !formData.city) {
      toast.error("Virhe", {
        description: "Täytä kaikki pakolliset kentät.",
      });
      return;
    }

    if (formData.type === "private" && (!formData.first_name || !formData.last_name)) {
      toast.error("Virhe", {
        description: "Yksityishenkilölle vaaditaan etu- ja sukunimi.",
      });
      return;
    }

    if ((formData.type === "company" || formData.type === "municipality") && !formData.company_name) {
      toast.error("Virhe", {
        description: "Yritykselle tai kunnalle vaaditaan yrityksen nimi.",
      });
      return;
    }

    if (formData.type === "estate" && (!formData.company_name || !formData.contact_person)) {
      toast.error("Virhe", {
        description: "Kuolinpesälle vaaditaan kuolinpesän nimi ja yhteyshenkilö.",
      });
      return;
    }

    if (selectedCustomer) {
      updateMutation.mutate({ id: selectedCustomer.id, data: formData as CustomerUpdate });
    } else {
      createMutation.mutate(formData as CustomerInsert);
    }
  };

  const handleDeleteClick = (customer: Customer) => {
    setCustomerToDelete(customer);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (customerToDelete) {
      deleteMutation.mutate(customerToDelete.id);
    }
  };

  const handleViewOrders = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsOrdersDialogOpen(true);
  };

  // Use centralized functions from formatters and constants

  return (
    <Layout>
      <div className="container mx-auto py-8 px-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Asiakkaat</h1>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="mr-2 h-4 w-4" />
            Lisää asiakas
          </Button>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Hae asiakkaita nimellä, sähköpostilla tai puhelinnumerolla..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-8">Ladataan asiakkaita...</div>
        ) : isError ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 mx-auto text-destructive mb-4" />
              <h3 className="text-lg font-medium">Asiakkaiden lataaminen epäonnistui</h3>
              <p className="text-muted-foreground mt-1">
                Yritä ladata sivu uudelleen.
              </p>
              <Button className="mt-4" variant="outline" onClick={() => window.location.reload()}>
                Lataa uudelleen
              </Button>
            </CardContent>
          </Card>
        ) : customers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">
                {searchQuery ? "Ei hakutuloksia" : "Ei asiakkaita"}
              </h3>
              <p className="text-muted-foreground mt-1">
                {searchQuery 
                  ? "Kokeile eri hakusanaa tai tyhjennä haku."
                  : "Lisää ensimmäinen asiakas aloittaaksesi."}
              </p>
              {!searchQuery && (
                <Button className="mt-4 gap-2" onClick={() => handleOpenDialog()}>
                  <Plus className="h-4 w-4" />
                  Lisää asiakas
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nimi</TableHead>
                  <TableHead>Tyyppi</TableHead>
                  <TableHead>Sähköposti</TableHead>
                  <TableHead>Puhelin</TableHead>
                  <TableHead>Kaupunki</TableHead>
                  <TableHead className="text-right">Toiminnot</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell className="font-medium">
                      <div>
                        {getCustomerDisplayName(customer)}
                        {customer.type === "estate" && customer.contact_person && (
                          <div className="text-sm text-muted-foreground">
                            c/o {customer.contact_person}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{CUSTOMER_TYPE_LABELS[customer.type] || customer.type}</TableCell>
                    <TableCell>{customer.email || "-"}</TableCell>
                    <TableCell>{customer.phone}</TableCell>
                    <TableCell>{customer.city}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewOrders(customer)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(customer)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteClick(customer)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {hasNextPage && (
              <div className="flex justify-center py-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? "Ladataan..." : "Lataa lisää"}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Customer Form Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) { handleCloseDialog(); createMutation.reset(); updateMutation.reset(); } else { setIsDialogOpen(true); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedCustomer ? "Muokkaa asiakasta" : "Lisää uusi asiakas"}
              </DialogTitle>
              <DialogDescription>
                Täytä asiakkaan tiedot. Pakolliset kentät on merkitty tähdellä (*).
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Tyyppi *</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) =>
                      setFormData({ ...formData, type: value as Customer["type"] })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="private">Yksityishenkilö</SelectItem>
                      <SelectItem value="company">Yritys</SelectItem>
                      <SelectItem value="municipality">Kunta</SelectItem>
                      <SelectItem value="estate">Kuolinpesä</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.type === "private" ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">Etunimi *</Label>
                      <Input
                        id="first_name"
                        value={formData.first_name || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, first_name: e.target.value })
                        }
                        required={formData.type === "private"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Sukunimi *</Label>
                      <Input
                        id="last_name"
                        value={formData.last_name || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, last_name: e.target.value })
                        }
                        required={formData.type === "private"}
                      />
                    </div>
                  </div>
                ) : formData.type === "estate" ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="company_name">Kuolinpesän nimi *</Label>
                      <Input
                        id="company_name"
                        value={formData.company_name || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, company_name: e.target.value })
                        }
                        placeholder="esim. Matti Meikäläisen kuolinpesä"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_person">Yhteyshenkilö (c/o) *</Label>
                      <Input
                        id="contact_person"
                        value={formData.contact_person || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, contact_person: e.target.value })
                        }
                        placeholder="esim. Maija Meikäläinen"
                        required
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company_name">
                          {formData.type === "municipality" ? "Kunnan nimi *" : "Yrityksen nimi *"}
                        </Label>
                        <Input
                          id="company_name"
                          value={formData.company_name || ""}
                          onChange={(e) =>
                            setFormData({ ...formData, company_name: e.target.value })
                          }
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="business_id">Y-tunnus</Label>
                        <Input
                          id="business_id"
                          value={formData.business_id || ""}
                          onChange={(e) =>
                            setFormData({ ...formData, business_id: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contact_person">Yhteyshenkilö</Label>
                      <Input
                        id="contact_person"
                        value={formData.contact_person || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, contact_person: e.target.value })
                        }
                        placeholder="Yhteyshenkilön nimi"
                      />
                    </div>
                  </>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Sähköposti</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email || ""}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Puhelin *</Label>
                    <Input
                      id="phone"
                      value={formData.phone || ""}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Osoite *</Label>
                  <Input
                    id="address"
                    value={formData.address || ""}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Katuosoite"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address_line2">Osoitteen lisätiedot</Label>
                  <Input
                    id="address_line2"
                    value={formData.address_line2 || ""}
                    onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                    placeholder="Rappukäytävä, kerros tms."
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="postal_code">Postinumero *</Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, postal_code: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city">Kaupunki *</Label>
                    <Input
                      id="city"
                      value={formData.city || ""}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Maa</Label>
                    <Select
                      value={formData.country || "FI"}
                      onValueChange={(value) => setFormData({ ...formData, country: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FI">Suomi</SelectItem>
                        <SelectItem value="SE">Ruotsi</SelectItem>
                        <SelectItem value="NO">Norja</SelectItem>
                        <SelectItem value="EE">Viro</SelectItem>
                        <SelectItem value="DE">Saksa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* E-invoicing section for companies, municipalities, and estates */}
                {(formData.type === "company" || formData.type === "municipality" || formData.type === "estate") && (
                  <div className="border-t pt-4 mt-4">
                    <h3 className="font-medium mb-3">Verkkolaskutus</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="einvoice_address">Verkkolaskuosoite</Label>
                        <Input
                          id="einvoice_address"
                          value={formData.einvoice_address || ""}
                          onChange={(e) =>
                            setFormData({ ...formData, einvoice_address: e.target.value })
                          }
                          placeholder="esim. 003712345678"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="einvoice_operator">Operaattorin tunnus</Label>
                        <Input
                          id="einvoice_operator"
                          value={formData.einvoice_operator || ""}
                          onChange={(e) =>
                            setFormData({ ...formData, einvoice_operator: e.target.value })
                          }
                          placeholder="esim. 003721291126"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Peruuta
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending
                    ? "Tallennetaan..."
                    : selectedCustomer
                    ? "Tallenna muutokset"
                    : "Lisää asiakas"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Vahvista poisto</AlertDialogTitle>
              <AlertDialogDescription>
                Haluatko varmasti poistaa asiakkaan{" "}
                <strong>{customerToDelete && getCustomerDisplayName(customerToDelete)}</strong>? Tätä
                toimintoa ei voi peruuttaa.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Peruuta</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Poistetaan..." : "Poista"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Customer Orders Dialog */}
        <Dialog open={isOrdersDialogOpen} onOpenChange={setIsOrdersDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Tilaushistoria: {selectedCustomer && getCustomerDisplayName(selectedCustomer)}
              </DialogTitle>
              <DialogDescription>
                Asiakkaan kaikki tilaukset järjestyksessä.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {customerOrders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Ei tilauksia.
                </div>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tilausnumero</TableHead>
                        <TableHead>Päivämäärä</TableHead>
                        <TableHead>Tila</TableHead>
                        <TableHead className="text-right">Summa</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customerOrders.map((order) => (
                        <TableRow key={order.id}>
                          <TableCell className="font-medium">{order.order_number}</TableCell>
                          <TableCell>{new Date(order.order_date).toLocaleDateString("fi-FI")}</TableCell>
                          <TableCell>{order.status}</TableCell>
                          <TableCell className="text-right">
                            {order.total_amount.toFixed(2)} €
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
